import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { observer } from "mobx-react";
import type { FieldProps } from "formik";
import { Formik, Form, Field } from "formik";
import { isEmpty } from "es-toolkit/compat";
import { useMutation } from "@apollo/client";
import { RESEND_VERIFY_EMAIL } from "api/mutations";
import { useStores } from "stores";
import { Text, Link } from "@chakra-ui/react";
import { Button } from "components/button/Button";
import { InputGroup } from "components/input/InputGroup";
import * as Yup from "yup";
import { Filler } from "components/filler/Filler";
import { RouterLink } from "components/routerLink/RouterLink";
import { Container } from "components/container/Container";
import { signUpComponentStyles } from "./registerComponents";

const emailValidation = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Required"),
});

const ResendEmailMessage = () => {
  const { authStore } = useStores();

  return (
    <Container {...signUpComponentStyles}>
      <Filler.SVGFiller
        imagePath="neutral/Click"
        variant="other"
        headline="Please check your email"
        mb="4"
      />
      <Text textAlign="center" textStyle="base">
        Verification email sent!
      </Text>
      <Button
        as={RouterLink}
        to="/login"
        type="submit"
        size="lg"
        maxW="unset"
        w="100%"
        m="40px 0 24px"
        onClick={() => {
          authStore.setResendEmail(false);
        }}
      >
        Log in
      </Button>
      <Text textStyle="caption" textAlign="center">
        Go back?
        <Link
          as={RouterLink}
          to="/login"
          ml="1"
          onClick={() => {
            authStore.setResendEmail(false);
          }}
          textStyle="captionLink"
        >
          Log in
        </Link>
      </Text>
    </Container>
  );
};

export const ResendEmail = observer(function ResendEmail() {
  const { authStore, userStore } = useStores();

  const { errors } = authStore;
  const [success, setSuccess] = useState(false);
  const [resendVerifyEmail, { data, error: resendVerifyEmailError, loading }] =
    useMutation(RESEND_VERIFY_EMAIL);

  useEffectOnce(() => {
    return () => authStore.resetError();
  });

  useEffect(() => {
    if (!isEmpty(data)) {
      setSuccess(true);
    }
  }, [data]);

  if (success) {
    return <ResendEmailMessage />;
  }

  const getErrorMessage = (errors: any) => {
    return (
      errors?.message &&
      (errors.message !== "Internal server error"
        ? errors.message[0].toUpperCase() + errors.message.substring(1)
        : "There seems to be some issue, please try again later.")
    );
  };

  return (
    <Formik
      initialValues={{ email: userStore.registrationForm?.email || "" }}
      validationSchema={emailValidation}
      onSubmit={async ({ email }, { setSubmitting }) => {
        setSubmitting(true);
        await resendVerifyEmail({
          variables: {
            email,
          },
        });
        setSubmitting(false);
        return;
      }}
    >
      {() => (
        <Form noValidate>
          <Container {...signUpComponentStyles}>
            <Filler.SVGFiller
              imagePath="neutral/Click"
              variant="other"
              headline={
                errors ? "You're already registered" : "Let's try this again"
              }
              mb="4"
              mt="0"
            />
            {errors ? (
              <Text mb="10" textAlign="center" textStyle="base">
                Looks like you've already been to the cloud. Enter your email
                below to receive another verification email.
              </Text>
            ) : (
              <Text mb="10" textAlign="center" textStyle="base">
                Enter your email address and we'll send another <br />
                verification message.
              </Text>
            )}

            <Field name="email">
              {({ field, meta }: FieldProps) => (
                <InputGroup
                  error={
                    getErrorMessage(errors) ||
                    getErrorMessage(resendVerifyEmailError) ||
                    (meta.touched && meta.error)
                  }
                  placeholder="Email address"
                  label="Email address"
                  {...field}
                />
              )}
            </Field>

            <Button
              data-cy="resend-verification-email"
              type="submit"
              size="lg"
              maxW="unset"
              w="100%"
              m="40px 0 24px"
              isLoading={loading}
            >
              Resend verification email
            </Button>

            <Text textAlign="center">
              Go back?
              <Link
                as={RouterLink}
                to="/login"
                ml="1"
                onClick={() => {
                  authStore.setResendEmail(false);
                }}
              >
                Log in
              </Link>
            </Text>
          </Container>
        </Form>
      )}
    </Formik>
  );
});
